import {
  Checkbox,
  CheckboxTypography,
  Form,
  FormGrid,
  FormGridItem,
  Link,
  MenuItem,
  Select,
  type SelectProps,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
  useEmailAddressValidator,
  useFirstNameValidator,
  useLastNameValidator,
  useMultilineTextValidator,
  usePhoneNumberValidator,
  useSelectValidator,
  useZipCodeValidator,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useMemo } from 'react';
import { constants } from '@/constants';
import type { ContactInfo } from '@/features/appointments/utils/ContactInfo';
import { getAceUserData } from '@/features/appointments/utils/getAceUserData';
import ProgressBar from './ProgressBar';

export type ContactInfoFields = keyof ContactInfo;

interface ContactInfoFormProps {
  fields: ContactInfoFields[];
  updateField: (contactInfo: ContactInfo) => void;
  formValues: ContactInfo;
  onSubmit: (data: unknown) => void;
  formHeader: string;
  subHeader?: string;
  continueButtonLabel?: string;
  fieldsProps?: {
    firstName?: TextFieldProps<'first-name'>;
    lastName?: TextFieldProps<'last-name'>;
    zipCode?: TextFieldProps<'zip-code'>;
    communicationPreference?: SelectProps;
    phoneNumber?: TextFieldProps<'phone-number'>;
    email?: TextFieldProps<'email-address'>;
    expectedTravelTime?: SelectProps;
    emailMessage?: TextFieldProps<'any-text'>;
    additionalInformation?: TextFieldProps<'any-text'>;
  };
}

type SelectOption = {
  label: string;
  value: string;
};

const typeOfTripOptions: SelectOption[] = [
  { label: 'Cruises', value: 'Cruise' },
  { label: 'Guided tours', value: 'Guided tour' },
  { label: 'Hotels', value: 'Hotel' },
  { label: 'Flights', value: 'Flight' },
  { label: 'Rental cars', value: 'Rental car' },
  { label: 'Building a vacation package', value: 'Build a vacation package' },
  { label: 'Day trips and activities', value: 'Day trips and activities' },
];

const typeOfTripOptionsMap: Record<string, string> = typeOfTripOptions.reduce(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {} as Record<string, string>,
);

function ContactInfoForm({
  fields,
  updateField,
  formValues,
  onSubmit,
  formHeader,
  subHeader,
  continueButtonLabel,
  fieldsProps,
}: ContactInfoFormProps) {
  const DEFAULT_WHEN_DO_YOU_PLAN_TO_TRAVEL: string = '0-3 Months';

  const firstNameValidator = useFirstNameValidator({ label: 'First name' });
  const lastNameValidator = useLastNameValidator({ label: 'Last name' });
  const zipCodeValidator = useZipCodeValidator({ label: 'ZIP code' });
  const selectValidator = useSelectValidator({
    label: 'How should we contact you?',
  });
  const phoneNumberValidator = usePhoneNumberValidator({
    label: 'Phone number',
  });
  const emailValidator = useEmailAddressValidator({ label: 'Email' });
  const emailMessageValidator = useMultilineTextValidator({
    label: 'Email message',
    required: true,
  });
  const additionalInfoValidator = useMultilineTextValidator({
    required: false,
  });

  const multiLineInputStyles = {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      border: 0,
      overflow: 'hidden',
    },
    '& .MuiTextField-root': {
      textAlign: 'left',
    },
    '& .MuiInputBase-inputMultiline': {
      border: '0 !important',
    },
  };

  const selectMenuProps: SelectProps['MenuProps'] = {
    sx: {
      '& .MuiMenu-paper': {
        maxHeight: {
          xs: 51 * 3,
          lg: 51 * 4,
        },
      },
    },
    // SCL prop
    PaperProps: { className: 'MuiSelect-Menu-paper' },
  };

  const fieldsMap: Record<keyof ContactInfo, () => JSX.Element> = {
    firstName: () => (
      <FormGridItem
        size={{
          xs: 12,
          lg: 6,
        }}
        key='firstName'
      >
        <TextField
          for='first-name'
          label='First name*'
          onChange={(e) =>
            updateField({ ...formValues, firstName: e.target.value })
          }
          value={formValues.firstName}
          validator={firstNameValidator}
          data-testid='firstName'
          {...fieldsProps?.firstName}
        />
      </FormGridItem>
    ),
    lastName: () => (
      <FormGridItem
        size={{
          xs: 12,
          lg: 6,
        }}
        key='lastName'
      >
        <TextField
          for='last-name'
          label='Last name*'
          onChange={(e) =>
            updateField({ ...formValues, lastName: e.target.value })
          }
          value={formValues.lastName}
          validator={lastNameValidator}
          data-testid='lastName'
          {...fieldsProps?.lastName}
        />
      </FormGridItem>
    ),
    zipCode: () => (
      <FormGridItem
        size={{
          xs: 12,
          lg: 6,
        }}
        key='zipCode'
      >
        <TextField
          for='zip-code'
          label='Zip code*'
          onChange={(e) =>
            updateField({ ...formValues, zipCode: e.target.value })
          }
          validator={zipCodeValidator}
          value={formValues.zipCode}
          data-testid='zipCode'
          slotProps={{
            htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
          }}
          {...fieldsProps?.zipCode}
        />
      </FormGridItem>
    ),
    communicationPreference: () => (
      <FormGridItem
        size={{ xs: 12, lg: 6 }}
        key='communicationPreference'
        data-testid='communicationPreferenceContainer'
      >
        <Select
          label='How should we contact you?'
          placeholder='Select one'
          onChange={(e) => {
            const newValue = e.target
              .value as ContactInfo['communicationPreference'];
            if (!newValue) return;
            updateField({ ...formValues, communicationPreference: newValue });
          }}
          defaultValue={formValues.communicationPreference}
          validator={selectValidator}
          name='communicationPreference'
          fullWidth={true}
          data-testid='communicationPreference'
          {...fieldsProps?.communicationPreference}
        >
          {(
            ['Phone', 'Email'] as NonNullable<
              ContactInfo['communicationPreference']
            >[]
          ).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormGridItem>
    ),
    phoneNumber: () => (
      <FormGridItem
        size={{
          xs: 12,
          lg: 6,
        }}
        key='phoneNumber'
      >
        <TextField
          for='phone-number'
          label='Phone number*'
          onChange={(e) =>
            updateField({ ...formValues, phoneNumber: e.target.value })
          }
          validator={phoneNumberValidator}
          value={formValues.phoneNumber}
          data-testid='phoneNumber'
          slotProps={{
            htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
          }}
          {...fieldsProps?.phoneNumber}
        />
      </FormGridItem>
    ),
    email: () => (
      <FormGridItem
        size={{
          xs: 12,
          lg: 6,
        }}
        key='email'
      >
        <TextField
          for='email-address'
          label='Email*'
          onChange={(e) =>
            updateField({ ...formValues, email: e.target.value })
          }
          validator={emailValidator}
          value={formValues.email}
          data-testid='email'
          {...fieldsProps?.email}
        />
      </FormGridItem>
    ),
    expectedTravelTime: () => (
      <FormGridItem
        size={{
          xs: 12,
          lg: 6,
        }}
        key='expectedTravelTime'
      >
        <Select
          label='When do you plan to travel'
          defaultValue={DEFAULT_WHEN_DO_YOU_PLAN_TO_TRAVEL}
          fullScreenThreshold={7}
          onChange={(e) =>
            updateField({ ...formValues, expectedTravelTime: e.target.value })
          }
          value={formValues.expectedTravelTime}
          name='expectedTravelTime'
          fullWidth={true}
          data-testid='expectedTravelTime'
          MenuProps={selectMenuProps}
          {...fieldsProps?.expectedTravelTime}
        >
          {[
            DEFAULT_WHEN_DO_YOU_PLAN_TO_TRAVEL,
            '3-6 Months',
            '6-9 Months',
            '9-12 Months',
            '12+ Months',
            'Not Sure',
          ].map((travelTime) => (
            <MenuItem key={travelTime} value={travelTime}>
              {travelTime}
            </MenuItem>
          ))}
        </Select>
      </FormGridItem>
    ),
    typeOfTrip: () => (
      <FormGridItem size={{ xs: 12, lg: 6 }} key='typeOfTrip'>
        <Select<string[]>
          label='What can we help you with?'
          placeholder='Types of trips'
          fullWidth
          multiple
          value={formValues.typeOfTrip || []}
          fullScreenThreshold={8}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) {
              return 'Types of trips';
            }
            return selected
              .map((value) => {
                const label = typeOfTripOptionsMap[value];
                return label ? label : value;
              })
              .join(', ');
          }}
          onChange={(e) => {
            const value = e.target.value as string[];
            updateField({ ...formValues, typeOfTrip: value });
          }}
          name='typeOfTrip'
          data-testid='typeOfTrip'
          MenuProps={selectMenuProps}
        >
          {typeOfTripOptions.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              <Checkbox
                checked={formValues.typeOfTrip?.includes(value) || false}
                variant='text'
                label={
                  <CheckboxTypography
                    sx={{
                      fontSize: '16px',
                    }}
                  >
                    {label}
                  </CheckboxTypography>
                }
                value={value}
                name={value}
                slotProps={{
                  formControlLabelProps: {
                    sx: {
                      outline: 'none !important',
                      p: '0 !important',
                      pointerEvents: 'none',
                    },
                  },
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormGridItem>
    ),
    emailMessage: () => (
      <FormGridItem size={12} key='emailMessage'>
        <TextField
          name={'emailMessage'}
          data-testid='emailMessage'
          onChange={(e) =>
            updateField({ ...formValues, emailMessage: e.target.value })
          }
          value={formValues.emailMessage}
          multiline
          fullWidth
          label={'Email message*'}
          placeholder={'Type a message to your advisor.'}
          validator={emailMessageValidator}
          sx={multiLineInputStyles}
          {...fieldsProps?.emailMessage}
        />
      </FormGridItem>
    ),
    additionalInformation: () => (
      <FormGridItem size={12} key='additionalInformation'>
        <TextField
          name={'additionalInformation'}
          data-testid='additionalInformation'
          onChange={(e) =>
            updateField({
              ...formValues,
              additionalInformation: e.target.value,
            })
          }
          value={formValues.additionalInformation}
          multiline
          fullWidth
          label={'Additional information or comments'}
          placeholder={''}
          validator={additionalInfoValidator}
          sx={multiLineInputStyles}
          {...fieldsProps?.additionalInformation}
        />
      </FormGridItem>
    ),
  };

  const aceUserData = useMemo(() => getAceUserData(), []);

  useEffect(() => {
    if (aceUserData && (!formValues.firstName || !formValues.lastName)) {
      updateField({
        ...formValues,
        firstName: aceUserData.firstName,
        lastName: aceUserData.lastName,
      });
    }
  }, [
    aceUserData,
    updateField,
    formValues,
    formValues.firstName,
    formValues.lastName,
  ]);

  return (
    <Form
      onSubmit={onSubmit}
      sx={{
        margin: 0,
        padding: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
      }}
      data-testid='contactInfoForm'
    >
      <Stack
        direction='column'
        sx={{
          width: { lg: '65%', xs: '85%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: '8px',
          flex: 1,
          pb: {
            xs: '80px',
            lg: '29px',
          },
          '& .MuiFormGrid-root': {
            width: '100%',
          },
        }}
      >
        <Stack
          direction='column'
          spacing={{
            xs: 0.5,
            md: 0,
          }}
          sx={{
            textAlign: {
              xs: 'left',
              md: 'center',
            },
          }}
        >
          <Typography data-testid='contactInfoHeader' variant='h4'>
            {formHeader}
          </Typography>
          {subHeader && (
            <Typography
              variant='b1Regular'
              sx={{
                fontSize: {
                  xs: '1.125rem',
                },
              }}
            >
              {subHeader}
            </Typography>
          )}
        </Stack>
        <FormGrid
          sx={{
            width: '100%',
            marginTop: {
              xs: '20px',
              md: '32px',
            },
            textAlign: 'left',
            '& .MuiTextField-root': {
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          {fields.map((field) => fieldsMap[field]())}
        </FormGrid>
        <Typography
          variant='b3Regular'
          sx={{
            textAlign: 'left',
            mt: 1,
          }}
        >
          By selecting "Next", I agree to receive calls and texts, using
          automated technology, from or on AAA's behalf regarding membership
          products/services and offers. Consent is not a condition of purchase.
          I agree to the{' '}
          <Link href={constants.PRIVACY_NOTICE_URL} target='_blank'>
            Privacy Notice
          </Link>
          ,{' '}
          <Link href={constants.TERMS_AND_CONDITIONS_URL} target='_blank'>
            Terms & Conditions
          </Link>
          , and{' '}
          <Link href={constants.TEXT_TERMS_URL} target='_blank'>
            Text Terms
          </Link>
          .
        </Typography>
      </Stack>
      <ProgressBar
        nextProgressValue={75}
        displayBackButton={true}
        isInsideForm
        fixed
        continueButtonLabel={continueButtonLabel}
      />
    </Form>
  );
}

export default ContactInfoForm;
